import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Paper,
  Typography,
  Button,
  LinearProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  TextField,
} from '@mui/material';
import { CloudUpload, CheckCircle, Error as ErrorIcon, Delete } from '@mui/icons-material';
import { toast } from 'react-toastify';
import apiService from '../services/api';
import { RENAISSANCE_COLORS } from '../utils';
import DatasetMappingConfig from './DatasetMappingConfig';

interface DatasetUploadProps {
  onUploadComplete?: (s3Key: string) => void;
}

interface UploadedFile {
  file: File;
  fileType: 'items' | 'responses';
  s3Key?: string;
  uploadUrl?: string;
  uploadFields?: Record<string, string>;
  progress: number;
  status: 'pending' | 'uploading' | 'uploaded' | 'error';
  error?: string;
  fileHash?: string;
}

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

const DatasetUpload: React.FC<DatasetUploadProps> = ({ onUploadComplete }) => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [s3Configured, setS3Configured] = useState<boolean | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [showMappingConfig, setShowMappingConfig] = useState(false);
  const [uploadedFileKeys, setUploadedFileKeys] = useState<Array<{s3Key: string; fileType: 'items' | 'responses'; fileHash?: string}>>([]);
  const [datasetName, setDatasetName] = useState<string>('');

  // Check S3 configuration on mount
  useEffect(() => {
    const checkConfig = async () => {
      try {
        const config = await apiService.checkDatasetsConfig();
        setS3Configured(config.configured);
      } catch (err) {
        setS3Configured(false);
      } finally {
        setLoadingConfig(false);
      }
    };
    checkConfig();
  }, []);

  // Calculate file hash using Web Crypto API
  const calculateFileHash = async (file: File): Promise<string> => {
    const arrayBuffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  };

  const handleFileTypeChange = (index: number, fileType: 'items' | 'responses') => {
    setFiles(prev => {
      const newFiles = [...prev];
      newFiles[index].fileType = fileType;
      return newFiles;
    });
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleMultiFileUpload = async () => {
    if (files.length === 0) {
      setError('Please select at least one file');
      return;
    }

    // Validate: exactly one items file
    const itemsFiles = files.filter(f => f.fileType === 'items');
    const responseFiles = files.filter(f => f.fileType === 'responses');

    if (itemsFiles.length !== 1) {
      setError('Exactly one file must be categorized as "items"');
      toast.error('Exactly one file must be categorized as "items"');
      return;
    }

    if (responseFiles.length === 0) {
      setError('At least one file must be categorized as "responses"');
      toast.error('At least one file must be categorized as "responses"');
      return;
    }

    setError(null);
    setSuccess(null);

    try {
      // Step 1: Calculate hashes for all files
      toast.info('Calculating file hashes...');
      const filesWithHashes = await Promise.all(
        files.map(async (fileInfo) => ({
          ...fileInfo,
          fileHash: await calculateFileHash(fileInfo.file)
        }))
      );

      setFiles(filesWithHashes);

      // Step 2: Request presigned URLs
      toast.info('Requesting upload URLs...');
      const uploadRequest = filesWithHashes.map(f => ({
        filename: f.file.name,
        file_type: f.fileType,
        file_hash: f.fileHash,
        content_type: f.file.type || 'text/csv'
      }));

      const uploadUrlsResponse = await apiService.requestMultipleUploadUrls(uploadRequest);

      // Update files with upload URLs
      const updatedFiles: UploadedFile[] = filesWithHashes.map((fileInfo, index) => {
        const urlResponse = uploadUrlsResponse.files[index];
        return {
          ...fileInfo,
          uploadUrl: urlResponse.upload_url,
          uploadFields: urlResponse.fields,
          s3Key: urlResponse.s3_key,
          status: (urlResponse.status === 'duplicate' ? 'uploaded' : 'pending') as 'pending' | 'uploading' | 'uploaded' | 'error'
        };
      });

      setFiles(updatedFiles);

      // Step 3: Upload files to S3
      const uploadPromises = updatedFiles.map(async (fileInfo, index) => {
        if (fileInfo.status === 'uploaded') {
          // File already exists, skip upload
          return fileInfo;
        }

        setFiles(prev => {
          const newFiles = [...prev];
          newFiles[index].status = 'uploading';
          return newFiles;
        });

        try {
          await apiService.uploadFileToS3(
            fileInfo.file,
            fileInfo.uploadUrl!,
            fileInfo.uploadFields!,
            (progress) => {
              setFiles(prev => {
                const newFiles = [...prev];
                newFiles[index].progress = progress;
                return newFiles;
              });
            }
          );

          setFiles(prev => {
            const newFiles = [...prev];
            newFiles[index].status = 'uploaded';
            newFiles[index].progress = 100;
            return newFiles;
          });

          return { ...fileInfo, status: 'uploaded' as const, progress: 100 };
        } catch (err: any) {
          setFiles(prev => {
            const newFiles = [...prev];
            newFiles[index].status = 'error';
            newFiles[index].error = err?.message || 'Upload failed';
            return newFiles;
          });
          throw err;
        }
      });

      await Promise.all(uploadPromises);

      // Step 4: Store uploaded file keys for mapping config
      const uploadedKeys = updatedFiles.map(f => ({
        s3Key: f.s3Key!,
        fileType: f.fileType,
        fileHash: f.fileHash
      }));
      setUploadedFileKeys(uploadedKeys);

      // Step 5: Show mapping configuration UI
      setShowMappingConfig(true);
      toast.success('All files uploaded successfully! Please configure column mappings and joins.');

    } catch (err: any) {
      const errorMessage = err?.message || 'Upload failed';
      setError(errorMessage);
      toast.error(`Upload failed: ${errorMessage}`);
    }
  };

  const handleMappingComplete = async (joinConfig: any, columnMappings: any, splitConfig?: any) => {
    try {
      toast.info('Starting join and conversion...');
      
      // Convert uploadedFileKeys to the format expected by the API
      const filesForApi = uploadedFileKeys.map(f => ({
        s3_key: f.s3Key,
        file_type: f.fileType,
        file_hash: f.fileHash
      }));
      
      const completeResponse = await apiService.notifyMultiUploadComplete(
        filesForApi,
        joinConfig,
        columnMappings,
        datasetName.trim() || undefined,  // Pass dataset name if provided
        splitConfig  // Pass split config if provided
      );

      setSuccess(`Files joined and converted successfully! Parquet file: ${completeResponse.parquet_key}`);
      toast.success('Join and conversion started. The Parquet file will be available shortly.');

      if (onUploadComplete) {
        onUploadComplete(completeResponse.parquet_key);
      }

      // Reset state
      setFiles([]);
      setUploadedFileKeys([]);
      setDatasetName('');
      setShowMappingConfig(false);
    } catch (err: any) {
      const errorMessage = err?.message || 'Join and conversion failed';
      setError(errorMessage);
      toast.error(`Join failed: ${errorMessage}`);
    }
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles: UploadedFile[] = [];
    
    for (const file of acceptedFiles) {
      // Validate file type
      if (!file.name.endsWith('.csv')) {
        setError('Only CSV files are allowed');
        toast.error('Only CSV files are allowed');
        continue;
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        setError(`File size exceeds maximum of ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
        toast.error(`File size exceeds maximum of ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
        continue;
      }

      newFiles.push({
        file,
        fileType: 'responses', // Default to responses, user can change
        progress: 0,
        status: 'pending'
      });
    }

    setFiles(prev => [...prev, ...newFiles]);
    setError(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
    },
    maxSize: MAX_FILE_SIZE,
    disabled: loadingConfig || !s3Configured || showMappingConfig,
  });

  if (showMappingConfig) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ color: 'primary.main' }}>
            Configure Column Mappings and Joins
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setShowMappingConfig(false);
              setFiles([]);
              setUploadedFileKeys([]);
            }}
          >
            Cancel
          </Button>
        </Box>
        <DatasetMappingConfig
          uploadedFiles={uploadedFileKeys}
          onComplete={handleMappingComplete}
          onCancel={() => {
            setShowMappingConfig(false);
            setFiles([]);
            setUploadedFileKeys([]);
          }}
        />
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
        Upload Dataset
      </Typography>

      {/* Dataset Name Input */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          label="Dataset Name (Optional)"
          value={datasetName}
          onChange={(e) => setDatasetName(e.target.value)}
          placeholder="Enter a friendly name for this dataset"
          disabled={loadingConfig || !s3Configured || files.length > 0}
          helperText="Give your dataset a memorable name for easier identification"
        />
      </Box>

      <Box
        {...getRootProps()}
        sx={{
          border: `2px dashed ${isDragActive ? RENAISSANCE_COLORS.blue : RENAISSANCE_COLORS.lightgrey}`,
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          cursor: (loadingConfig || !s3Configured || files.length > 0) ? 'not-allowed' : 'pointer',
          backgroundColor: isDragActive ? RENAISSANCE_COLORS.beige : 'transparent',
          transition: 'all 0.3s ease',
          opacity: (loadingConfig || !s3Configured || files.length > 0) ? 0.6 : 1,
        }}
      >
        <input {...getInputProps()} />
        <CloudUpload sx={{ fontSize: 48, color: RENAISSANCE_COLORS.mediumgrey, mb: 2 }} />
        {isDragActive ? (
          <Typography variant="body1" sx={{ color: RENAISSANCE_COLORS.blue }}>
            Drop CSV files here...
          </Typography>
        ) : (
          <>
            <Typography variant="body1" gutterBottom>
              Drag and drop CSV files here, or click to select
            </Typography>
            <Typography variant="body2" sx={{ color: RENAISSANCE_COLORS.mediumgrey, mt: 1 }}>
              Maximum file size: {MAX_FILE_SIZE / (1024 * 1024)}MB per file
            </Typography>
            <Typography variant="body2" sx={{ color: RENAISSANCE_COLORS.mediumgrey, mt: 1 }}>
              You can upload multiple files. One file should be categorized as "items", others as "responses".
            </Typography>
          </>
        )}
      </Box>

      {files.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Selected Files ({files.length})
          </Typography>
          <List>
            {files.map((fileInfo, index) => (
              <ListItem
                key={index}
                sx={{
                  border: '1px solid',
                  borderColor: RENAISSANCE_COLORS.lightgrey,
                  borderRadius: 1,
                  mb: 1,
                  bgcolor: 'white'
                }}
              >
                <ListItemText
                  primary={fileInfo.file.name}
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      <FormControl size="small" sx={{ minWidth: 120, mr: 2 }}>
                        <InputLabel>File Type</InputLabel>
                        <Select
                          value={fileInfo.fileType}
                          label="File Type"
                          onChange={(e) => handleFileTypeChange(index, e.target.value as 'items' | 'responses')}
                          disabled={fileInfo.status === 'uploading' || fileInfo.status === 'uploaded'}
                        >
                          <MenuItem value="items">Items</MenuItem>
                          <MenuItem value="responses">Responses</MenuItem>
                        </Select>
                      </FormControl>
                      <Chip
                        label={fileInfo.fileType}
                        size="small"
                        color={fileInfo.fileType === 'items' ? 'primary' : 'default'}
                        sx={{ mr: 1 }}
                      />
                      <Typography variant="caption" sx={{ color: RENAISSANCE_COLORS.mediumgrey }}>
                        {(fileInfo.file.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                    </Box>
                  }
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {fileInfo.status === 'uploading' && (
                    <Box sx={{ width: 100 }}>
                      <LinearProgress variant="determinate" value={fileInfo.progress} />
                      <Typography variant="caption">{Math.round(fileInfo.progress)}%</Typography>
                    </Box>
                  )}
                  {fileInfo.status === 'uploaded' && (
                    <CheckCircle color="success" />
                  )}
                  {fileInfo.status === 'error' && (
                    <ErrorIcon color="error" />
                  )}
                  <IconButton
                    size="small"
                    onClick={() => removeFile(index)}
                    disabled={fileInfo.status === 'uploading'}
                  >
                    <Delete />
                  </IconButton>
                </Box>
              </ListItem>
            ))}
          </List>

          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleMultiFileUpload}
              disabled={files.some(f => f.status === 'uploading') || files.length === 0}
            >
              Upload Files
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setFiles([]);
                setError(null);
                setSuccess(null);
              }}
            >
              Clear All
            </Button>
          </Box>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} icon={<ErrorIcon />}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mt: 2 }} icon={<CheckCircle />}>
          {success}
        </Alert>
      )}
    </Paper>
  );
};

export default DatasetUpload;
